// Stage 17: Homunculus Module — Settings tab.
//
// Mirrors MemoryAdvTab.tsx style. Renders:
//   - Enable switch + short explainer.
//   - Worker-reachability badge (public healthcheck ping).
//   - Settings (wake TTL, sleep-idle minutes, max engrams per wake,
//     read-lip depth, profile row cap).
//   - Calibration snapshot from the most recent wake.
//   - Inspector: paginated list with type filter, includeSuperseded /
//     includeForgotten toggles, per-row Forget (operator-mode).
//   - Actions: Wake now, Sleep now, Export NDJSON, Session report.
//   - Operator mode: paste token → per-row Forget unlocks.

import { useEffect, useState, useCallback } from 'react';
import { apiGet, apiJson } from '../api/client';
import {
  healthcheck,
  listResidue,
  wakeNow,
  sleepNow,
  operatorForget,
  exportUrl,
  sessionReportUrl,
  HomunculusEngram,
  HomunculusHealth,
  EngramType,
} from '../api/homunculus';

interface SettingsResponse {
  settings: Record<string, string>;
}

const API_BASE = import.meta.env.VITE_API_BASE as string;

type TypeFilter = 'all' | EngramType;

const BADGE: Record<EngramType, { bg: string; fg: string; label: string }> = {
  'pocket-recognition': { bg: '#d1fae5', fg: '#065f46', label: 'Pocket' },
  'closeout-warning': { bg: '#ffe4e6', fg: '#9f1239', label: 'Avoid' },
  'trajectory-template': { bg: '#e0f2fe', fg: '#075985', label: 'Template' },
  'wipeout-residue': { bg: '#fef3c7', fg: '#92400e', label: 'Wipe' },
  'calibration-snapshot': { bg: '#f1f5f9', fg: '#334155', label: 'Cal' },
};

function formatAgo(ts: number): string {
  if (!ts) return 'never';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n) + '…' : s;
}

export function HomunculusTab({
  onChanged,
  currentConversationId,
}: {
  onChanged: () => void;
  currentConversationId?: string | null;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [health, setHealth] = useState<HomunculusHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);

  // Settings (strings in D1; numbers in UI).
  const [wakeTTL, setWakeTTL] = useState(10);
  const [sleepIdle, setSleepIdle] = useState(45);
  const [maxPerWake, setMaxPerWake] = useState(200);
  const [depth, setDepth] = useState(5);
  const [cap, setCap] = useState(10000);
  const [vecLast, setVecLast] = useState(0);

  // Inspector.
  const [filter, setFilter] = useState<TypeFilter>('all');
  const [inclSup, setInclSup] = useState(false);
  const [inclFor, setInclFor] = useState(false);
  const [rows, setRows] = useState<HomunculusEngram[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);

  // Operator mode.
  const [opToken, setOpToken] = useState('');
  const [opUnlocked, setOpUnlocked] = useState(false);

  // Async events.
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Session context for wake/sleep/session-report actions.
  const [sessionIdInput, setSessionIdInput] = useState('');

  // Calibration from most recent wake (if any).
  const [lastWakeCal, setLastWakeCal] = useState<
    null | {
      accuracy: number | null;
      trend: string;
      fidelity: number;
      biasFlags: string[];
      ceiling: string | null;
      ampl: number | null;
    }
  >(null);

  const loadSettings = useCallback(async (): Promise<void> => {
    try {
      const res = await apiGet<SettingsResponse>('/api/settings');
      const s = res.settings;
      setEnabled(s.homunculus_enabled === 'true');
      setWakeTTL(Number(s.homunculus_wake_ttl_minutes ?? '10'));
      setSleepIdle(Number(s.homunculus_sleep_idle_minutes ?? '45'));
      setMaxPerWake(Number(s.homunculus_max_engrams_per_wake ?? '200'));
      setDepth(Number(s.homunculus_read_lip_default_depth ?? '5'));
      setCap(Number(s.homunculus_max_engrams ?? '10000'));
      setVecLast(Number(s.homunculus_vectorize_last_run ?? '0'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const pingHealth = useCallback(async (): Promise<void> => {
    try {
      const h = await healthcheck(API_BASE);
      setHealth(h);
      setHealthErr(null);
    } catch (e) {
      setHealthErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const loadRows = useCallback(
    async (reset: boolean): Promise<void> => {
      if (!enabled) return;
      setLoadingRows(true);
      try {
        const r = await listResidue({
          type: filter === 'all' ? undefined : filter,
          includeSuperseded: inclSup,
          includeForgotten: inclFor,
          cursor: reset ? undefined : (cursor ?? undefined),
          limit: 50,
        });
        setRows((prev) => (reset ? r.engrams : [...prev, ...r.engrams]));
        setCursor(r.nextCursor);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setLoadingRows(false);
      }
    },
    [enabled, filter, inclSup, inclFor, cursor],
  );

  useEffect(() => {
    void loadSettings();
    void pingHealth();
  }, [loadSettings, pingHealth]);

  // Stage 19: pre-populate the session-id input with the currently-open
  // conversation id on mount (or when the id becomes available later).
  // Guarded by `!sessionIdInput.trim()` so user-typed values are never
  // clobbered; sessionIdInput is intentionally NOT in the deps list —
  // including it would create a loop racing against keystrokes.
  useEffect(() => {
    if (currentConversationId && !sessionIdInput.trim()) {
      setSessionIdInput(currentConversationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  useEffect(() => {
    setRows([]);
    setCursor(null);
    if (enabled) void loadRows(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filter, inclSup, inclFor]);

  async function toggleEnabled(next: boolean): Promise<void> {
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      await apiJson('PUT', '/api/settings', {
        homunculus_enabled: next ? 'true' : 'false',
      });
      setEnabled(next);
      setInfo(
        next ? 'Homunculus enabled.' : 'Homunculus disabled (data preserved).',
      );
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveSetting(key: string, value: string): Promise<void> {
    try {
      await apiJson('PUT', '/api/settings', { [key]: value });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function doWake(): Promise<void> {
    if (!sessionIdInput.trim()) {
      setErr('Enter a session id (e.g. a conversation id) first.');
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await wakeNow(sessionIdInput.trim());
      setLastWakeCal({
        accuracy: r.calibration.lastKnownAccuracy,
        trend: r.calibration.trendVector,
        fidelity: r.calibration.fidelityRecommendation,
        biasFlags: r.calibration.biasFlags,
        ceiling: r.calibration.lastCeilingEstimate,
        ampl: r.calibration.lastLoopAmplification,
      });
      setInfo(
        `Woke session ${sessionIdInput.trim()} (residue: ${
          r.residue.pocketRecognitions.length +
          r.residue.closeoutWarnings.length +
          r.residue.trajectoryTemplates.length +
          r.residue.recentWipeouts.length
        } engrams, ${r.durationMs}ms)`,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function doSleep(): Promise<void> {
    if (!sessionIdInput.trim()) {
      setErr('Enter a session id first.');
      return;
    }
    setBusy(true);
    setErr(null);
    setInfo(null);
    try {
      const r = await sleepNow(sessionIdInput.trim());
      setInfo(
        `Slept session ${sessionIdInput.trim()}: ${r.engramsCommitted} engrams, ${r.wipeoutsRecorded} wipeouts, next-wake context ${r.nextWakeContextSize}`,
      );
      await loadRows(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function doForget(engramId: string): Promise<void> {
    if (!opUnlocked || !opToken) {
      setErr('Operator token not verified.');
      return;
    }
    const reason = window.prompt(
      `Forget engram ${engramId.slice(0, 8)}…\n\nAudit trail requires a short reason (≤ 500 chars):`,
      '',
    );
    if (!reason || !reason.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await operatorForget(engramId, reason.trim(), opToken);
      if (r.forgotten) {
        setInfo(`Forgot engram ${engramId.slice(0, 8)}…`);
      } else {
        setInfo(`Engram ${engramId.slice(0, 8)}… not found.`);
      }
      await loadRows(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function doExport(): void {
    window.open(exportUrl(API_BASE), '_blank', 'noopener');
  }

  function doSessionReport(): void {
    if (!sessionIdInput.trim()) {
      setErr('Enter a session id first.');
      return;
    }
    window.open(
      sessionReportUrl(API_BASE, sessionIdInput.trim(), 'markdown'),
      '_blank',
      'noopener',
    );
  }

  return (
    <div className="panel">
      <h3>Homunculus (agent-continuity residue)</h3>
      <p className="muted">
        Preserves non-propositional navigational competence across sessions —
        pocket regions, closeout zones, wipeouts, calibration. Companion to
        the advanced memory module. Disabling is non-destructive: stored
        engrams remain and re-appear when re-enabled.
      </p>

      <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <input
            type="checkbox"
            checked={enabled === true}
            disabled={enabled === null || busy}
            onChange={(e) => void toggleEnabled(e.target.checked)}
          />
          Enable Homunculus
        </label>
        {busy ? <span className="muted small">working…</span> : null}
      </div>

      <div className="small muted" style={{ marginTop: 8 }}>
        <strong>Worker reachability:</strong>{' '}
        {health ? (
          <span>
            ok · {health.service}@{health.version} · region {health.region}
          </span>
        ) : healthErr ? (
          <span className="status-missing">unreachable: {healthErr}</span>
        ) : (
          <em>checking…</em>
        )}
      </div>

      {err ? <div className="status-missing">Error: {err}</div> : null}
      {info ? <div className="muted small">{info}</div> : null}

      {enabled ? (
        <>
          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Settings</h4>
            <div className="row" style={{ gap: '1rem', flexWrap: 'wrap' }}>
              <label>
                Wake TTL (min):{' '}
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={wakeTTL}
                  style={{ width: '4rem' }}
                  onChange={(e) => setWakeTTL(Number(e.target.value) || 10)}
                  onBlur={() =>
                    void saveSetting(
                      'homunculus_wake_ttl_minutes',
                      String(wakeTTL),
                    )
                  }
                />
              </label>
              <label>
                Sleep idle (min):{' '}
                <input
                  type="number"
                  min={1}
                  max={1440}
                  value={sleepIdle}
                  style={{ width: '5rem' }}
                  onChange={(e) => setSleepIdle(Number(e.target.value) || 45)}
                  onBlur={() =>
                    void saveSetting(
                      'homunculus_sleep_idle_minutes',
                      String(sleepIdle),
                    )
                  }
                />
              </label>
              <label>
                Engrams / wake:{' '}
                <input
                  type="number"
                  min={10}
                  max={1000}
                  value={maxPerWake}
                  style={{ width: '5rem' }}
                  onChange={(e) =>
                    setMaxPerWake(Number(e.target.value) || 200)
                  }
                  onBlur={() =>
                    void saveSetting(
                      'homunculus_max_engrams_per_wake',
                      String(maxPerWake),
                    )
                  }
                />
              </label>
              <label>
                Read-lip depth:{' '}
                <input
                  type="number"
                  min={1}
                  max={20}
                  value={depth}
                  style={{ width: '4rem' }}
                  onChange={(e) => setDepth(Number(e.target.value) || 5)}
                  onBlur={() =>
                    void saveSetting(
                      'homunculus_read_lip_default_depth',
                      String(depth),
                    )
                  }
                />
              </label>
              <label>
                Row cap:{' '}
                <input
                  type="number"
                  min={100}
                  max={100_000}
                  value={cap}
                  style={{ width: '6rem' }}
                  onChange={(e) => setCap(Number(e.target.value) || 10000)}
                  onBlur={() =>
                    void saveSetting('homunculus_max_engrams', String(cap))
                  }
                />
              </label>
            </div>
            <p className="muted small" style={{ marginTop: '0.5rem' }}>
              Last vectorize drain: {formatAgo(vecLast)}.
            </p>
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Calibration snapshot</h4>
            {lastWakeCal ? (
              <div className="small">
                <div>
                  Accuracy:{' '}
                  {lastWakeCal.accuracy !== null
                    ? lastWakeCal.accuracy.toFixed(2)
                    : '—'}{' '}
                  · trend {lastWakeCal.trend} · fidelity{' '}
                  {lastWakeCal.fidelity.toFixed(2)}
                </div>
                <div className="muted">
                  Loop amplification: {lastWakeCal.ampl ?? '—'} · ceiling:{' '}
                  {lastWakeCal.ceiling ?? '—'}
                </div>
                <div
                  className="muted"
                  style={{
                    color:
                      lastWakeCal.biasFlags.length > 0 ? '#9f1239' : undefined,
                  }}
                >
                  Bias flags:{' '}
                  {lastWakeCal.biasFlags.length > 0
                    ? lastWakeCal.biasFlags.join(', ')
                    : 'none'}
                </div>
              </div>
            ) : (
              <div className="muted small">
                Wake a session below to populate the snapshot.
              </div>
            )}
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Actions (session-scoped)</h4>
            <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="session id (conversation id)"
                value={sessionIdInput}
                onChange={(e) => setSessionIdInput(e.target.value)}
                style={{ width: '18rem', fontFamily: 'monospace' }}
              />
              <button
                onClick={() => {
                  if (currentConversationId)
                    setSessionIdInput(currentConversationId);
                }}
                disabled={!currentConversationId || busy}
                title="Fill with the currently-open conversation id"
              >
                Use current
              </button>
              <button onClick={() => void doWake()} disabled={busy}>
                Wake now
              </button>
              <button onClick={() => void doSleep()} disabled={busy}>
                Sleep now
              </button>
              <button onClick={doSessionReport} disabled={busy}>
                Session report
              </button>
              <button onClick={doExport} disabled={busy}>
                Export NDJSON
              </button>
            </div>
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Inspector</h4>
            <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
              <label>
                Filter:{' '}
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as TypeFilter)}
                >
                  <option value="all">all</option>
                  <option value="pocket-recognition">pocket</option>
                  <option value="closeout-warning">closeout</option>
                  <option value="trajectory-template">template</option>
                  <option value="wipeout-residue">wipeout</option>
                  <option value="calibration-snapshot">calibration</option>
                </select>
              </label>
              <label style={{ display: 'inline-flex', gap: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={inclSup}
                  onChange={(e) => setInclSup(e.target.checked)}
                />
                include superseded
              </label>
              <label style={{ display: 'inline-flex', gap: '0.25rem' }}>
                <input
                  type="checkbox"
                  checked={inclFor}
                  onChange={(e) => setInclFor(e.target.checked)}
                />
                include forgotten
              </label>
            </div>

            <ul
              role="list"
              style={{ listStyle: 'none', padding: 0, marginTop: '0.75rem' }}
            >
              {rows.map((r) => {
                const badge = BADGE[r.type] ?? {
                  bg: '#e5e7eb',
                  fg: '#1f2937',
                  label: r.type,
                };
                // Stage 19: fieldAnchor is already a parsed object on
                // the API response — no JSON.parse. Guard against a
                // null/undefined envelope belt-and-braces.
                const fa = r.fieldAnchor ?? {
                  observedIn: [],
                  failedIn: [],
                  neutralIn: [],
                };
                return (
                  <li
                    key={r.id}
                    role="listitem"
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      padding: '0.5rem 0',
                    }}
                  >
                    <div
                      className="row"
                      style={{ gap: '0.5rem', alignItems: 'center' }}
                    >
                      <span
                        className="hom-badge"
                        style={{
                          background: badge.bg,
                          color: badge.fg,
                          padding: '0.1rem 0.5rem',
                          borderRadius: 4,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        <span aria-hidden="true">{badge.label.toUpperCase()}</span>
                        <span className="sr-only"> {badge.label}</span>
                      </span>
                      <code
                        title={r.id}
                        style={{ fontSize: '0.8rem', color: '#6b7280' }}
                      >
                        {r.id.slice(0, 8)}
                      </code>
                      {r.shapeKey ? (
                        <code style={{ fontSize: '0.8rem' }}>
                          {r.shapeKey}
                        </code>
                      ) : null}
                      <span className="small muted">
                        obs:{fa.observedIn.length} / fail:{fa.failedIn.length} /
                        neu:{fa.neutralIn.length}
                      </span>
                      {opUnlocked ? (
                        <button
                          onClick={() => void doForget(r.id)}
                          disabled={busy}
                          title="Operator-only soft-delete"
                          style={{ marginLeft: 'auto' }}
                        >
                          Forget
                        </button>
                      ) : null}
                    </div>
                    <div
                      className="small"
                      style={{ marginTop: '0.25rem', opacity: 0.9 }}
                    >
                      {truncate(r.pattern?.phenomenalTag ?? '', 200)}
                    </div>
                    {r.supersededBy ? (
                      <div className="muted small">
                        superseded by{' '}
                        <code>{r.supersededBy.slice(0, 8)}</code>
                      </div>
                    ) : null}
                    {r.forgottenAt ? (
                      <div className="muted small">
                        forgotten at{' '}
                        {new Date(r.forgottenAt * 1000).toLocaleString()}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            {cursor ? (
              <button
                onClick={() => void loadRows(false)}
                disabled={loadingRows}
                style={{ marginTop: '0.5rem' }}
              >
                Load more
              </button>
            ) : null}
            {rows.length === 0 && !loadingRows ? (
              <div className="muted small">No engrams match.</div>
            ) : null}
          </div>

          <div className="panel" style={{ marginTop: '1rem' }}>
            <h4 style={{ marginTop: 0 }}>Operator mode</h4>
            <p className="muted small" style={{ marginTop: 0 }}>
              Paste the operator token to unlock per-row Forget actions.
              Not persisted; clears when you leave the tab.
            </p>
            <div className="row" style={{ gap: '0.5rem' }}>
              <input
                type="password"
                placeholder="HOMUNCULUS_OPERATOR_TOKEN"
                autoComplete="off"
                value={opToken}
                onChange={(e) => {
                  setOpToken(e.target.value);
                  setOpUnlocked(false);
                }}
                style={{ width: '22rem', fontFamily: 'monospace' }}
                aria-label="Homunculus operator token"
              />
              <button
                onClick={() => setOpUnlocked(!!opToken)}
                disabled={!opToken}
              >
                {opUnlocked ? 'Unlocked' : 'Unlock'}
              </button>
              {opUnlocked ? (
                <span className="small muted">Forget buttons enabled.</span>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
